import { OrganizationPublicId, PrivacyCenterPublicId } from "../../../common/service/server/Types";
import React, { useRef, useState } from "react";
import { Api } from "../../../common/service/Api";
import { Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import { Field, Form } from "react-final-form";
import { CloseableDialogTitle } from "./CloseableDialogTitle";
import { LoadingButton } from "../../../common/components/LoadingButton";
import { useActionRequest } from "../../../common/hooks/api";
import { PrivacyCenterFontDto } from "../../../common/service/server/dto/PrivacyCenterDto";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

export const FONT_FILE_TYPES = ["font/otf", "font/ttf"];

interface FontUploadDialogProps {
  organizationId: OrganizationPublicId;
  privacyCenterId: PrivacyCenterPublicId;
  open: boolean;
  onClose: () => void;
  onDone: (newFont: PrivacyCenterFontDto) => void;
}

export const FontUploadDialog = ({
  organizationId,
  privacyCenterId,
  open,
  onClose,
  onDone,
}: FontUploadDialogProps) => {
  const apiFn = (fontFile: File) =>
    Api.privacyCenter.postPrivacyCenterFont(organizationId, privacyCenterId, fontFile);

  const [fontFile, setFontFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const fontInputRef = useRef<HTMLInputElement | null>(null);

  const { fetch: postFont, request: postFontRequest } = useActionRequest({
    api: () => apiFn(fontFile),
    messages: {
      forceError: "Unable to upload font, please try again later",
      success: "Font successfully uploaded.",
    },
    onSuccess: (result) => {
      onDone(result);
      setFontFile(null);
      onClose();
    },
    notReady: fontFile == null,
  });

  const handleFileChange = (onChange) => (event) => {
    if (event.target?.files?.length > 0) {
      const file = event.target?.files[0];
      setFontFile(file);
    }
    onChange(event);
  };

  const removeFont = () => {
    setFontFile(null);
  };

  return (
    <Dialog
      className="logo-upload-dialog"
      fullWidth={true}
      maxWidth="md"
      open={open}
      onClose={() => {
        setFontFile(null);
        onClose();
      }}
    >
      <Form
        onSubmit={postFont}
        validate={() => {
          const errors = {};
          if (fontFile && fontFile.size > MAX_FILE_SIZE_BYTES) {
            errors["imageFile"] = "File must be less than 10MiB in size";
          }

          if (fontFile && !FONT_FILE_TYPES.includes(fontFile.type)) {
            errors["imageFile"] = "File must be an accepted font type";
          }

          return errors;
        }}
        render={({ handleSubmit, errors }) => {
          return (
            <form onSubmit={handleSubmit}>
              <CloseableDialogTitle
                onClose={() => {
                  setFontFile(null);
                  onClose();
                }}
                subtitle={
                  <Typography variant="subtitle2" component="span" style={{ display: "block" }}>
                    File type needs to be .OTF or .TTF only
                  </Typography>
                }
              >
                Upload Fonts
              </CloseableDialogTitle>

              <DialogContent>
                <div className="logo-upload-dialog--content">
                  <div
                    className={`logo-image-preview ${
                      dragOver ? "logo-upload-dialog--dragover" : ""
                    }`}
                    id="logo-image-preview"
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={(event) => {
                      event.preventDefault();
                      setDragOver(false);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      let file;
                      if (event.dataTransfer.items) {
                        // Use DataTransferItemList interface to access the file(s)
                        [...Array.from(event.dataTransfer.items)].forEach((item) => {
                          // If dropped items aren't files, reject them
                          if (!file && item.kind === "file") {
                            file = item.getAsFile();
                          }
                        });
                      } else {
                        // Use DataTransfer interface to access the file(s)
                        [...Array.from(event.dataTransfer.files)].forEach((f) => {
                          if (!file) {
                            file = f;
                          }
                        });
                      }
                      if (file) {
                        const container = new DataTransfer();
                        container.items.add(file);
                        fontInputRef.current.files = container.files;
                        setFontFile(file);
                        fontInputRef.current.dispatchEvent(new Event("change"));
                      }
                      setDragOver(false);
                    }}
                  >
                    {fontFile && <div className="logo-image-preview--image">{fontFile.name}</div>}
                    {!fontFile && (
                      <>
                        <div className="logo-image-preview--droptext">
                          Drag and drop your file here
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-lg w-100">
                    <Field name="imageFile">
                      {({ input: { onChange, ...inputProps }, meta: { error } }) => {
                        const handleChange = handleFileChange(onChange);
                        return (
                          <>
                            <div className="flex-row flex--justify-center">
                              <input
                                hidden={true}
                                id="logo-image-file-input"
                                ref={fontInputRef}
                                {...inputProps}
                                onChange={handleChange}
                                type="file"
                                accept={FONT_FILE_TYPES.join(",")}
                              />
                              <label htmlFor="logo-image-file-input">
                                <span className="logo-upload-dialog--input-remove">
                                  <Button
                                    className="w-192 mr-md"
                                    onClick={removeFont}
                                    disabled={!fontFile}
                                    size="large"
                                  >
                                    Remove
                                  </Button>
                                </span>
                                <span className="logo-upload-dialog--input-add">
                                  <Button
                                    className="w-192"
                                    variant="contained"
                                    onClick={() => fontInputRef.current?.click()}
                                    size="large"
                                  >
                                    Choose Font
                                  </Button>
                                </span>
                              </label>
                            </div>
                            {error && (
                              <div className="mt-md text-center text-error text-t2 ml-md">
                                {error}
                              </div>
                            )}
                          </>
                        );
                      }}
                    </Field>
                  </div>
                </div>
              </DialogContent>

              <DialogActions>
                <LoadingButton
                  className="w-192"
                  type="submit"
                  color="primary"
                  loading={postFontRequest.running}
                  disabled={!fontFile || errors?.imageFile}
                >
                  Upload
                </LoadingButton>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};
